import clsx from 'clsx'
import { FC, MouseEventHandler, useCallback } from 'react'
import { ToBottomArrow, ToTopArrow } from './assets'
import s from './Pagination.module.scss'
import { useTranslate } from '@hooks/useTranslation'
import { useAppState } from '@context/state'
import { AppStateTypes } from '@context/reducer'
import { CwsButton, CwsP } from '@components/cws'

export interface PaginationProps {
  currentPage: number
  pagesCount: number
  goToPage: (p: number) => () => void
  onLoadMore?: () => any
  allPagesLoaded?: boolean
  moreItemsToShow?: number
}

export const Pagination: FC<PaginationProps> = (props) => {
  const { currentPage, pagesCount, goToPage, onLoadMore, allPagesLoaded = false, moreItemsToShow } = props
  const { translate } = useTranslate()
  const { dispatch } = useAppState()

  const renderPageButton = useCallback(
    (page: number | null) => {
      const navigateTo = page
      const isActive = currentPage === navigateTo

      return (
        <CwsButton
          size="tiny"
          variant={isActive ? 'primary-dark' : 'secondary'}
          onClick={navigateTo ? goToPage(navigateTo) : undefined}
        >
          {navigateTo ?? '...'}
        </CwsButton>
      )
    },
    [currentPage, goToPage]
  )

  const pagesToRender = [
    ...new Array(pagesCount)
      .fill(0)
      .map((_, index) => index + 1)
      .slice(currentPage - 3 < 0 ? 0 : currentPage - 3, currentPage + 2),
  ].filter((value) => !!value)

  const goToTopButtonNode = (
    <div className={clsx('flex flex-row w-7 h-7 items-center', 'select-none cursor-pointer')} onClick={goToPage(1)}>
      <span className={clsx('font-medium text-sm leading-5', 'm-auto select-none overflow-hidden')}>
        <ToTopArrow />
      </span>
    </div>
  )

  const goToBottomButtonNode = (
    <div
      className={clsx('flex flex-row w-7 h-7 items-center', 'select-none cursor-pointer')}
      onClick={goToPage(pagesCount)}
    >
      <span className={clsx('font-medium text-sm leading-5', 'm-auto select-none overflow-hidden')}>
        <ToBottomArrow />
      </span>
    </div>
  )

  const dotsNode = (
    <div className={clsx('flex flex-row w-7 h-7 items-center', 'select-none')}>
      <span className={clsx('font-medium text-sm leading-5', 'm-auto select-none overflow-hidden')}>{'...'}</span>
    </div>
  )

  const loadMoreHandler: MouseEventHandler<HTMLButtonElement> = () => {
    dispatch({ type: AppStateTypes.setTopOffset, payload: 0 })
    if (typeof onLoadMore === 'function') onLoadMore()
  }

  return (
    <div className={s.container}>
      <div className={s.controls}>
        {currentPage >= 4 && (
          <>
            {goToTopButtonNode}
            {dotsNode}
          </>
        )}

        {pagesToRender.map(renderPageButton)}

        {/*{pagesCount >= 4 && currentPage <= pagesCount - 3 && (*/}
        {/*  <>*/}
        {/*    {dotsNode}*/}
        {/*    {goToBottomButtonNode}*/}
        {/*  </>*/}
        {/*)}*/}
      </div>
      <div
        className="
        flex flex-col laptop:flex-row items-center laptop:items-baseline flex-1-auto
        m-auto space-y-4 laptop:justify-end laptop:space-y-0 laptop:space-x-6 w-full
      "
      >
        {(moreItemsToShow === 0 || !!moreItemsToShow) && moreItemsToShow > 0 && (
          <CwsP>{translate('w2.category.scroll.items.to.show', { x: Math.abs(moreItemsToShow) })}</CwsP>
        )}
        {/* <span className="font-normal text-midnight text-sm laptop:text-base leading-5 laptop:leading-5.5">
          103 more items to show
        </span> */}
        {allPagesLoaded ? (
          <CwsP>{translate('w2.category.all.items.loaded')}</CwsP>
        ) : (
          <CwsButton onClick={loadMoreHandler} size="medium" variant="secondary">
            {translate('w2.category.scroll.load.items.action')}
          </CwsButton>
        )}
      </div>
    </div>
  )
}

export default Pagination
